import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/config';
import { useAdmin } from '../context/AdminContext';
import '../css/MainPage.css';
import { getFilterParams } from '../utils/apiUtils';
import AuthLayout from '../components/AuthLayout';
import { clearAuthStorage } from '../context/AuthSessionContext';

const RESET_STEP_NUMBER = { id: 1, auth: 2, password: 3 };

const MainPage = () => {
    const [guardian_id, setGuardianId] = useState(''); // 보호자용 아이디
    const [guardian_pw, setGuardianPassword] = useState(''); // 보호자용 패스워드
    const [error, setError] = useState('');

    // 비밀번호 찾기 관련 상태
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    const [resetId, setResetId] = useState('');
    const [resetStep, setResetStep] = useState('id'); // 'id', 'auth', 'password'
    const [authResult, setAuthResult] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState('');

    // 아이디 찾기 관련 상태
    const [showIdFind, setShowIdFind] = useState(false);
    const [idFindStep, setIdFindStep] = useState('auth'); // 'auth', 'result'
    const [idFindError, setIdFindError] = useState('');
    const [foundLoginId, setFoundLoginId] = useState('');

    const navigate = useNavigate();
    const { updateAdminInfo } = useAdmin();



    // postMessage 이벤트 리스너 추가
    useEffect(() => {
        const handleMessage = (event) => {
            // console.log('[MainPage] postMessage 수신:', event);
            // console.log('[MainPage] event.origin:', event.origin);
            // console.log('[MainPage] event.data:', event.data);

            if (event.origin !== window.location.origin) {
                // console.log('[MainPage] origin 불일치, 무시');
                return;
            }
            // payload 또는 직접 값 모두 지원
            const payload = event.data && event.data.payload ? event.data.payload : event.data;
            const { rslt_name, tel_no, birthday, sex_cd, di, eci } = payload || {};
            let { authType } = payload || {};
            // authType 누락 시 현재 열린 모달 상태로 유추
            if (!authType) {
                if (showIdFind) authType = 'find_id';
                else if (showPasswordReset) authType = 'password_reset';
            }

            // console.log('[MainPage] 파싱된 데이터:', {
            //     rslt_name, tel_no, birthday, sex_cd, di, eci, authType
            // });

            // 비밀번호 찾기 (기존 호환: authType 없을 때도 허용)
            if (rslt_name && tel_no && authType === 'password_reset') {
                // console.log('[MainPage] 비밀번호 찾기 인증 결과 처리');
                setAuthResult({ name: rslt_name, mobile: tel_no, birthday, sex_cd, di, eci });

                // 본인인증 완료 후 DB 검증 수행 (resetId를 직접 전달)
                handleAuthVerification(resetId, rslt_name, tel_no, birthday, sex_cd, di, eci);
            } else if (rslt_name && tel_no && authType === 'find_id') {
                // 아이디 찾기
                const maskMobile = (m) => (m ? `${m.slice(0, 3)}****${m.slice(-4)}` : '');
                // console.log('[IDFIND] 본인인증 결과 수신', {
                //     name: rslt_name ? `${rslt_name[0]}*` : '',
                //     mobile: maskMobile(tel_no),
                //     birthday,
                //     hasEci: !!eci
                // });
                handleIdFindVerification(rslt_name, tel_no, birthday, sex_cd, di, eci);
            } else {
                // console.log('[MainPage] 조건 불일치:', {
                //     hasRsltName: !!rslt_name,
                //     hasTelNo: !!tel_no,
                //     authType,
                //     isPasswordReset: authType === 'password_reset'
                // });
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [resetId, showIdFind, showPasswordReset]);

    const handleLogin = async (e) => {
        e.preventDefault();

        try {
            const response = await axios.post(`${API_BASE_URL}/api/guardian-login`, {
                guardian_id,
                guardian_pw
            }, {
                withCredentials: true
            });

            if (response.data.status === 'success') {
                const { token, user } = response.data;
                // 이전 로그인(관리자 등)의 권한 값이 남지 않게 먼저 비운다.
                clearAuthStorage();
                localStorage.setItem('token', token);
                localStorage.setItem('id', user.id);
                localStorage.setItem('user_name', user.name);
                localStorage.setItem('user_role', user.role);
                localStorage.setItem('user', JSON.stringify(user));
                navigate('/GuardianControlCenter');
            } else {
                setError(response.data.message || '로그인 실패');
            }
        } catch (error) {
            setError(error.response?.data?.message || '로그인 중 오류가 발생했습니다.');
        }
    };

    const navigateToSignup = () => {
        navigate('/signup?type=guardian');
    };

    // 비밀번호 찾기 관련 함수들
    const handlePasswordReset = () => {
        setShowPasswordReset(true);
        setResetStep('id');
        setResetError('');
        setResetSuccess('');
        setResetId('');
        setNewPassword('');
        setConfirmPassword('');
        setAuthResult(null);
    };

    // 아이디 찾기 열기
    const handleOpenIdFind = () => {
        setShowIdFind(true);
        setIdFindStep('auth');
        setIdFindError('');
        setFoundLoginId('');
        // console.log('[IDFIND] 아이디 찾기 모달 열림');
    };

    const handleResetIdSubmit = async (e) => {
        e.preventDefault();
        if (!resetId.trim()) {
            setResetError('아이디를 입력해주세요.');
            return;
        }

        try {
            // console.log('[MainPage] ID 존재 여부 확인:', resetId);

            const response = await axios.post(`${API_BASE_URL}/api/guardian-check-id`, {
                guardian_id: resetId
            });

            if (response.data.status === 'success') {
                // console.log('[MainPage] ID 확인 성공, 본인인증 단계로 이동');
                setResetStep('auth');
                setResetError('');
            } else {
                // console.log('[MainPage] ID 확인 실패:', response.data.message);
                setResetError(response.data.message || '등록되지 않은 아이디입니다.');
            }
        } catch (error) {
            // console.error('[MainPage] ID 확인 오류:', error);
            setResetError(error.response?.data?.message || '아이디 확인 중 오류가 발생했습니다.');
        }
    };

    const handleAuthPopup = () => {
        const popupUrl = `${API_BASE_URL}/auth/popup2?view=web&redirectUrl=${encodeURIComponent(`${window.location.origin}/cert/kcb/popup3`)}&origin=${encodeURIComponent(window.location.origin)}&authType=password_reset`;
        window.open(popupUrl, 'auth_popup', 'width=500,height=600');
    };

    // 아이디 찾기용 본인인증 팝업
    const handleIdFindAuthPopup = () => {
        const popupUrl = `${API_BASE_URL}/auth/popup2?view=web&redirectUrl=${encodeURIComponent(`${window.location.origin}/cert/kcb/popup3`)}&origin=${encodeURIComponent(window.location.origin)}&authType=find_id`;
        // console.log('[IDFIND] 본인인증 팝업 오픈', { url: popupUrl });
        window.open(popupUrl, 'auth_popup_find_id', 'width=500,height=600');
    };

    const handleAuthVerification = async (currentResetId, authName, authMobile, authBirthday, authSexCd, authDi, authEci) => {
        try {
            // console.log('[MainPage] DB 검증 시작:', { currentResetId, authName, authMobile, authBirthday });

            // resetId가 비어있는지 확인
            if (!currentResetId || currentResetId.trim() === '') {
                // console.log('[MainPage] resetId가 비어있음');
                setResetError('아이디를 먼저 입력해주세요.');
                setResetStep('id'); // ID 입력 단계로 되돌림
                return;
            }

            const response = await axios.post(`${API_BASE_URL}/api/guardian-verify-auth`, {
                guardian_id: currentResetId,
                auth_name: authName,
                auth_mobile: authMobile,
                auth_birthday: authBirthday,
                auth_sex_cd: authSexCd,
                auth_di: authDi,
                auth_eci: authEci
            });

            if (response.data.status === 'success') {
                // console.log('[MainPage] DB 검증 성공, 비밀번호 변경 단계로 이동');
                setResetStep('password');
                setResetError('');
            } else {
                // console.log('[MainPage] DB 검증 실패:', response.data.message);
                setResetError(response.data.message || '본인인증 정보가 등록된 정보와 일치하지 않습니다.');
                setResetStep('auth'); // 인증 단계로 되돌림
            }
        } catch (error) {
            // console.error('[MainPage] DB 검증 오류:', error);
            setResetError(error.response?.data?.message || '본인인증 검증 중 오류가 발생했습니다.');
            setResetStep('auth'); // 인증 단계로 되돌림
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (!newPassword || !confirmPassword) {
            setResetError('새 비밀번호를 입력해주세요.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setResetError('비밀번호가 일치하지 않습니다.');
            return;
        }

        if (newPassword.length < 6) {
            setResetError('비밀번호는 6자 이상이어야 합니다.');
            return;
        }

        try {
            const response = await axios.post(`${API_BASE_URL}/api/guardian-reset-password`, {
                guardian_id: resetId,
                new_password: newPassword
            });

            if (response.data.status === 'success') {
                setResetSuccess('비밀번호가 성공적으로 변경되었습니다.');
                setTimeout(() => {
                    setShowPasswordReset(false);
                    setResetSuccess('');
                }, 2000);
            } else {
                setResetError(response.data.message || '비밀번호 변경에 실패했습니다.');
            }
        } catch (error) {
            setResetError(error.response?.data?.message || '비밀번호 변경 중 오류가 발생했습니다.');
        }
    };

    const closePasswordReset = () => {
        setShowPasswordReset(false);
        setResetStep('id');
        setResetError('');
        setResetSuccess('');
        setResetId('');
        setNewPassword('');
        setConfirmPassword('');
        setAuthResult(null);
    };

    // 아이디 찾기 검증 및 결과 조회
    const handleIdFindVerification = async (authName, authMobile, authBirthday, authSexCd, authDi, authEci) => {
        try {
            setIdFindError('');
            setFoundLoginId('');
            // 서버에 인증 결과 전달하여 guardian_master에서 일치하는 login_id 조회
            const maskMobile = (m) => (m ? `${m.slice(0, 3)}****${m.slice(-4)}` : '');
            // console.log('[IDFIND] 아이디 조회 요청', {
            //     name: authName ? `${authName[0]}*` : '',
            //     mobile: maskMobile(authMobile),
            //     hasBirthday: !!authBirthday,
            //     hasEci: !!authEci
            // });
            const response = await axios.post(`${API_BASE_URL}/api/guardian-find-id`, {
                auth_name: authName,
                auth_mobile: authMobile,
                auth_birthday: authBirthday,
                auth_sex_cd: authSexCd,
                auth_di: authDi,
                auth_eci: authEci
            });

            if (response.data.status === 'success') {
                setFoundLoginId(response.data.login_id);
                setIdFindStep('result');
                const loginId = response.data.login_id || '';
                const maskedId = loginId.length > 2 ? `${loginId.slice(0, 2)}***` : (loginId ? `${loginId[0]}*` : '');
                // console.log('[IDFIND] 아이디 조회 성공', { login_id: maskedId });
            } else {
                setIdFindError(response.data.message || '일치하는 정보가 없습니다.');
                // console.log('[IDFIND] 아이디 조회 실패', { message: response.data.message });
            }
        } catch (error) {
            setIdFindError(error.response?.data?.message || '아이디 찾기 중 오류가 발생했습니다.');
            // console.error('[IDFIND] 아이디 조회 예외', {
            //     message: error.response?.data?.message || error.message
            // });
        }
    };

    const closeIdFind = () => {
        setShowIdFind(false);
        setIdFindStep('auth');
        setIdFindError('');
        setFoundLoginId('');
    };

    return (
        <AuthLayout
            eyebrow="보호자"
            title="보호자 로그인"
            description="가족의 건강 기록을 함께 확인하세요."
            footer={(
                <>
                    처음 오셨나요?{' '}
                    <button type="button" className="auth-page__textlink auth-page__textlink--primary" onClick={navigateToSignup}>회원가입</button>
                </>
            )}
        >
            <form onSubmit={handleLogin} className="auth-page__form" noValidate>
                <div className="sds-field">
                    <label className="sds-label" htmlFor="guardian-login-id">아이디</label>
                    <input
                        id="guardian-login-id"
                        className={`sds-input${error ? ' sds-input--error' : ''}`}
                        type="text"
                        placeholder="아이디를 입력하세요"
                        value={guardian_id}
                        onChange={(e) => setGuardianId(e.target.value)}
                        autoComplete="username"
                        aria-invalid={error ? 'true' : undefined}
                        aria-describedby={error ? 'guardian-login-error' : undefined}
                    />
                </div>
                <div className="sds-field">
                    <label className="sds-label" htmlFor="guardian-login-password">비밀번호</label>
                    <input
                        id="guardian-login-password"
                        className={`sds-input${error ? ' sds-input--error' : ''}`}
                        type="password"
                        placeholder="비밀번호를 입력하세요"
                        value={guardian_pw}
                        onChange={(e) => setGuardianPassword(e.target.value)}
                        autoComplete="current-password"
                        aria-invalid={error ? 'true' : undefined}
                        aria-describedby={error ? 'guardian-login-error' : undefined}
                    />
                    {error && <p className="sds-field-error" id="guardian-login-error" role="alert">{error}</p>}
                </div>
                <button type="submit" className="sds-btn sds-btn--primary">로그인</button>
                <div className="auth-page__textlinks">
                    <button type="button" className="auth-page__textlink" onClick={handleOpenIdFind}>아이디 찾기</button>
                    <button type="button" className="auth-page__textlink" onClick={handlePasswordReset}>비밀번호 찾기</button>
                </div>
            </form>

            {/* 비밀번호 찾기 모달 */}
            {showPasswordReset && (
                <div className="sds-modal-overlay">
                    <div className="sds-modal auth-page__modal" role="dialog" aria-modal="true" aria-labelledby="password-reset-title">
                        <div className="sds-modal__head">
                            <div>
                                <p className="auth-page__eyebrow">비밀번호 찾기 · 3단계 중 {RESET_STEP_NUMBER[resetStep]}단계</p>
                                <h2 id="password-reset-title" className="sds-modal__title">비밀번호 찾기</h2>
                            </div>
                        </div>
                        <div className="auth-page__steps" aria-hidden="true">
                            {[1, 2, 3].map((n) => (
                                <span key={n} className={`auth-page__step${n <= RESET_STEP_NUMBER[resetStep] ? ' is-done' : ''}`} />
                            ))}
                        </div>
                        <div className="sds-modal__body">
                            {resetStep === 'id' && (
                                <form id="password-reset-id-form" onSubmit={handleResetIdSubmit} className="auth-page__form">
                                    <p className="auth-page__desc">가입 시 사용한 아이디를 입력해주세요.</p>
                                    <div className="sds-field">
                                        <label className="sds-label" htmlFor="password-reset-id">아이디</label>
                                        <input id="password-reset-id" className="sds-input" type="text" placeholder="아이디 입력" value={resetId} onChange={(e) => setResetId(e.target.value)} />
                                    </div>
                                </form>
                            )}
                            {resetStep === 'auth' && (
                                <p className="auth-page__desc">본인인증을 진행해주세요.</p>
                            )}
                            {resetStep === 'password' && (
                                <form id="password-reset-password-form" onSubmit={handlePasswordChange} className="auth-page__form">
                                    <p className="auth-page__desc">새로운 비밀번호를 입력해주세요.</p>
                                    <div className="sds-field">
                                        <label className="sds-label" htmlFor="password-reset-new">새 비밀번호</label>
                                        <input id="password-reset-new" className="sds-input" type="password" placeholder="새 비밀번호" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                                        <p className="auth-page__hint">6자 이상</p>
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label" htmlFor="password-reset-confirm">새 비밀번호 확인</label>
                                        <input id="password-reset-confirm" className="sds-input" type="password" placeholder="새 비밀번호 확인" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                                    </div>
                                </form>
                            )}
                            {resetError && <p className="sds-field-error" role="alert">{resetError}</p>}
                            {resetSuccess && <p className="auth-page__ok" role="status">{resetSuccess}</p>}
                        </div>
                        <div className="sds-modal__foot">
                            {resetStep === 'id' && (<>
                                <button type="button" className="sds-btn sds-btn--secondary" onClick={closePasswordReset}>취소</button>
                                <button type="submit" form="password-reset-id-form" className="sds-btn sds-btn--primary">다음</button>
                            </>)}
                            {resetStep === 'auth' && (<>
                                <button type="button" className="sds-btn sds-btn--secondary" onClick={() => setResetStep('id')}>이전</button>
                                <button type="button" className="sds-btn sds-btn--primary" onClick={handleAuthPopup}>본인인증</button>
                            </>)}
                            {resetStep === 'password' && (<>
                                <button type="button" className="sds-btn sds-btn--secondary" onClick={closePasswordReset}>취소</button>
                                <button type="submit" form="password-reset-password-form" className="sds-btn sds-btn--primary">비밀번호 변경</button>
                            </>)}
                        </div>
                    </div>
                </div>
            )}

            {/* 아이디 찾기 모달 */}
            {showIdFind && (
                <div className="sds-modal-overlay">
                    <div className="sds-modal auth-page__modal" role="dialog" aria-modal="true" aria-labelledby="id-find-title">
                        <div className="sds-modal__head">
                            <h2 id="id-find-title" className="sds-modal__title">아이디 찾기</h2>
                        </div>
                        <div className="sds-modal__body">
                            {idFindStep === 'auth' && (
                                <p className="auth-page__desc">본인인증을 진행해주세요.</p>
                            )}
                            {idFindStep === 'result' && (
                                <>
                                    <p className="auth-page__desc">고객님의 아이디는 아래와 같습니다.</p>
                                    <p className="auth-page__found">{foundLoginId}</p>
                                </>
                            )}
                            {idFindError && <p className="sds-field-error" role="alert">{idFindError}</p>}
                        </div>
                        <div className="sds-modal__foot">
                            {idFindStep === 'auth' && (<>
                                <button type="button" className="sds-btn sds-btn--secondary" onClick={closeIdFind}>닫기</button>
                                <button type="button" className="sds-btn sds-btn--primary" onClick={handleIdFindAuthPopup}>본인인증</button>
                            </>)}
                            {idFindStep === 'result' && (
                                <button type="button" className="sds-btn sds-btn--primary" onClick={closeIdFind}>확인</button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AuthLayout>
    );
};

export default MainPage;
